import React, { FC } from 'react';
import { KatFileUpload } from '@amzn/katal-react';
import { MaterialRadioButtonGroup } from 'src/components/general/MaterialRadioButtonGroup';
import { ConfirmationDialog } from 'src/components/general/ConfirmationDialog';
import { StatusIndicator } from 'src/components/general/StatusIndicator';
import { useAssetInput } from 'src/hooks/assetInput/useAssetInput';
import { InputType, AssetStatus } from 'src/types';
import { UrlInput } from './UrlInput';
import { SearchImageInput } from 'src/components/pages/campaignAutomation/input/assets/SearchImageInput';
import { AssetInputProps, INPUT_OPTIONS } from '.';
import styles from './AssetInput.module.css';
import { getFileUploadState } from 'src/utils/assetInput/assetInputUtils';
import { AssetConstants, StateConstants } from 'src/constants';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { CMSConfig } from 'src/configs/campaignMetadataConfig';
import { PropUtils } from 'src/utils/PropUtils';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { VideoDurationInput } from './VideoDurationInput';
import { AssetType, TransformationTemplate } from '@amzn/fire-tv-campaign-automation-service';
import { LogoTextInput } from './LogoTextInput';

const ENABLED_STATUSES_FOR_VIDEO_DURATION = [
  AssetStatus.COMPLETED,
  AssetStatus.PENDING_TRANSFORMATION,
];

const SEARCH_ENABLED_STATUSES: readonly AssetStatus[] = [
  AssetStatus.FAILED,
  AssetStatus.COMPLETED,
  AssetStatus.PENDING_INPUT,
  AssetStatus.BASIC_VALIDATION_FAILED,
];

const LOADING_STATUSES: readonly AssetStatus[] = [
  AssetStatus.PENDING_UPLOAD,
  AssetStatus.TRANSFORMATION_IN_PROGRESS,
];

/**
 * Component for handling asset input through various methods (URL, file upload, or logo text)
 * @param {AssetInputProps} props - The component props
 * @returns {JSX.Element} Asset input component with radio buttons for input type selection
 */
export const AssetInput: FC<AssetInputProps> = ({
  label,
  assetInputOptions,
  assetInputHandler,
  acceptedFileTypes,
  maxFileSizeInBytes,
  assetTransformationHandler,
  error,
  status,
  assetType,
  assetDetails,
  assetValidationHandler,
  setState,
  disabled = false,
}) => {
  const {
    selectedInputType,
    url,
    showConfirmDialog,
    radioGroupName,
    isValidUrlInput,
    selectedImageTitle,
    handleUrlChange,
    handleUrlFetch,
    handleCatalogSelection,
    handleUrlRemoval,
    handleFileAttached,
    handleFileRemoved,
    handleFileReplaced,
    handleFileAttachedError,
    handleRadioChange,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    handleTransformationTemplateChange,
    handleClearSelectedImage,
    getConfirmationMessage,
  } = useAssetInput({
    label,
    assetType,
    assetInputHandler,
    status,
    setState,
    assetDetails,
    assetValidationHandler,
    assetTransformationHandler,
  });

  const { assetStates } = useAssetContext();
  const fileUploadStatus = getFileUploadState(status, error);
  const radioButtonInputOptions = Object.keys(assetInputOptions)
    .map((option) => INPUT_OPTIONS[option as InputType])
    .filter((option): option is NonNullable<typeof option> => option !== undefined);

  const renderInput = () => {
    switch (selectedInputType) {
      case InputType.URL:
        return (
          <UrlInput
            url={url}
            status={status}
            onChange={handleUrlChange}
            onFetch={handleUrlFetch}
            onClear={handleUrlRemoval}
            isValidUrl={isValidUrlInput}
            isFetching={status === AssetStatus.PENDING_UPLOAD}
            disabled={disabled}
          />
        );
      case InputType.UPLOAD:
        return (
          <KatFileUpload
            accept={acceptedFileTypes}
            maxSize={maxFileSizeInBytes}
            onFilesAttached={(event) => handleFileAttached(event)}
            onFilesRemoved={handleFileRemoved}
            onFilesReplaced={(event) => handleFileReplaced(event)}
            onFilesAttachedError={handleFileAttachedError}
            hideClearAll
            state={fileUploadStatus}
            disabled={disabled}
            data-testid="file-upload"
          >
            <div slot="error">{error}</div>
          </KatFileUpload>
        );
      case InputType.SEARCH:
        return (
          <SearchImageInput
            label="Search by Title or Keyword"
            placeholder="Enter movie or content name"
            onImageSelect={(imageUrl: string, imageTitle?: string) =>
              handleCatalogSelection(imageUrl, imageTitle)
            }
            selectedImageUrl={url}
            selectedImageTitle={selectedImageTitle}
            onClearSelection={handleClearSelectedImage}
            maxLength={AssetConstants.SEARCH_QUERY_MAX_LENGTH}
            disabled={disabled || !SEARCH_ENABLED_STATUSES.includes(status)}
          />
        );
      case InputType.LOGO_TEXT:
        return (
          <LogoTextInput
            logoTextState={assetStates[assetType]}
            updateAssetState={setState}
            disabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container} data-testid="asset-input">
      {label && <label className={styles.assetTitle}>{label}</label>}

      <MaterialRadioButtonGroup
        name={radioGroupName}
        options={radioButtonInputOptions}
        value={selectedInputType}
        onChange={handleRadioChange}
        data-testid="input-type-selector"
        disabled={disabled}
      />

      {assetInputOptions[selectedInputType as InputType] && (
        <StyledDropdown
          options={PropUtils.createOptionsFromEnumValues(
            CMSConfig.TRANSFORMATION_TEMPLATE[assetType],
          )}
          value={assetStates[assetType]?.transformationTemplate}
          onChange={handleTransformationTemplateChange}
          label={'Transformation Template'}
          data-testid={`${assetType}-transformation-template-selector`}
          disabled={disabled}
        />
      )}

      {renderInput()}

      <StatusIndicator
        // for Input type of file upload input error is thrown in KatFileUpload component
        error={selectedInputType !== InputType.UPLOAD ? error : StateConstants.EMPTY_STRING}
        isLoading={LOADING_STATUSES.includes(status)}
        isSuccess={status === AssetStatus.COMPLETED}
        data-testid="asset-input-status"
      />

      {assetType === AssetType.VIDEO_TRAILER &&
        assetStates[assetType]?.transformationTemplate !==
          TransformationTemplate.NO_TRANSFORMATION && (
          <VideoDurationInput
            startTime={assetStates[assetType]?.startTime || ''}
            endTime={assetStates[assetType]?.endTime || ''}
            onStartTimeChange={(time) => {
              setState({
                startTime: time,
                status: AssetStatus.PENDING_TRANSFORMATION,
              });
            }}
            onEndTimeChange={(time) => {
              setState({
                endTime: time,
                status: AssetStatus.PENDING_TRANSFORMATION,
              });
            }}
            videoDuration={assetStates[assetType]?.videoDuration}
            disabled={!ENABLED_STATUSES_FOR_VIDEO_DURATION.includes(status)}
            data-testid="video-duration-input"
          />
        )}

      <ConfirmationDialog
        isVisible={showConfirmDialog}
        title="Change Input"
        message={getConfirmationMessage()}
        onConfirm={handleConfirmTypeChange}
        onCancel={handleCancelTypeChange}
        data-testid="asset-input-confirm-dialog"
      />
    </div>
  );
};
